import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import Icon from '@xo-union/tk-component-icons';
import GdsInvitation from '~/glm/models/gds/gds-invitation-model';
import GdsUiState from '~/glm/models/gds/gds-ui-state';
import GdsEvent from '~/glm/models/gds/gds-event-model';
import GdsGuest from '~/glm/models/gds/gds-guest-model';
import GuestListStore from '~/glm/models/guest-list-store';
import GuestCollection from '~/glm/models/gds/gds-guest-collection';
import classnames from 'classnames';
import OutsideClickHandler from '@xo-union/component-outside-click';
import RsvpDropdown from '~/glm/components/guest-list/cells/rsvp-status-dropdown';
import AnalyticsStore from '~/glm/models/analytics-store';
import GuestStatus from '~/glm/components/shared/guest-status';
import dropdownStyles from './dropdown.scss';
import cellStyles from './rsvp-cell.scss';

const colorStatusMapping = {
  'Not Invited': '#9699A0',
  Invited: '#CACCD0',
  Attending: '#58BF81',
  Declined: '#000000',
};

const rsvpStatusMapping = {
  Invited: null,
  Attending: true,
  Declined: false,
};

const getColorStatus = gdsInvitation => {
  if (!gdsInvitation) return colorStatusMapping['Not Invited'];
  return colorStatusMapping[gdsInvitation.displayRsvp];
};

@inject('guests', 'guestListStore')
@observer
class RsvpCell extends Component {
  static propTypes = {
    gdsInvitation: PropTypes.instanceOf(GdsInvitation),
    guests: PropTypes.instanceOf(GuestCollection),
    guestListStore: PropTypes.instanceOf(GuestListStore).isRequired,
    gdsUiState: PropTypes.instanceOf(GdsUiState).isRequired,
    guest: PropTypes.instanceOf(GdsGuest).isRequired,
    event: PropTypes.instanceOf(GdsEvent),
    disabled: PropTypes.bool,
    analyticsStore: PropTypes.instanceOf(AnalyticsStore),
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      isEditing: false,
      showWarning: false,
    };
  }

  handleClick = rsvpStatus => {
    const { gdsInvitation, analyticsStore } = this.props;

    analyticsStore.track('Dashboard Interaction', {
      action: 'update rsvp status',
      decisionArea: 'body',
      rsvpStatus,
    });

    if (!gdsInvitation && rsvpStatus !== 'Not Invited') {
      return this.createRsvp(rsvpStatus);
    } else if (gdsInvitation) {
      return this.editRsvp(rsvpStatus);
    }
  };

  createRsvp = rsvpStatus => {
    const { guest, event } = this.props;
    const householdId = guest.householdId;
    const guestId = guest.id;
    const rsvp = rsvpStatusMapping[rsvpStatus];
    const newInvitation = new GdsInvitation(
      {
        eventId: event.id,
        invitationSent: false,
        rsvp,
      },
      householdId,
      guestId
    );

    return newInvitation.post().then(invitation => {
      guest.addInvitation(invitation, householdId, guestId);
    });
  };

  editRsvp = rsvpStatus => {
    const { gdsInvitation } = this.props;
    let rsvpValue = null;

    if (rsvpStatus === 'Attending') {
      rsvpValue = true;
    } else if (rsvpStatus === 'Declined') {
      rsvpValue = false;
    } else if (rsvpStatus === 'Not Invited') {
      return this.deleteInvitation();
    }

    this.setState({ isEditing: false });
    if (gdsInvitation.rsvp !== rsvpValue) {
      return gdsInvitation.patch({ rsvp: rsvpValue });
    }
  };

  deleteInvitation = () => {
    const { gdsInvitation, guest } = this.props;

    guest.removeInvitation(gdsInvitation);
    return gdsInvitation.delete();
  };

  renderOptions = () => <RsvpDropdown updateRsvpStatus={this.handleClick} currentSelection={'attending'} />;

  handleInteraction = () => {
    this.setState({ isEditing: !this.state.isEditing });
  };

  render() {
    const { gdsInvitation, disabled = false } = this.props;
    return (
      <OutsideClickHandler onOutsideClick={() => this.setState({ isEditing: false })}>
        <div className={classnames(cellStyles.rsvp)}>
          <div
            className={classnames('btn-group drop-down', { open: this.state.isEditing }, dropdownStyles.dropdown)}
            role="listbox"
            tabIndex={0}
            onKeyDown={e => (e.key === 'Enter' ? this.handleInteraction() : null)}
            onClick={this.handleInteraction}
          >
            <span>
              <GuestStatus
                color={getColorStatus(gdsInvitation)}
                label={gdsInvitation ? gdsInvitation.displayRsvp : 'Not Invited'}
              />
              <Icon className={dropdownStyles.rsvpArrow} name={'caret_down'} size="sm" />
            </span>
            {this.state.isEditing && (
              <ul className={dropdownStyles.menu} role="menu">
                {disabled ? null : this.renderOptions()}
              </ul>
            )}
          </div>
        </div>
      </OutsideClickHandler>
    );
  }
}

export default RsvpCell;
